import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/lib/utils";

const GRAVATAR_SIZE = 128;

async function gravatarUrl(email: string) {
  const bytes = new TextEncoder().encode(email.trim().toLowerCase());
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  return `https://gravatar.com/avatar/${hash}?s=${GRAVATAR_SIZE}&d=404`;
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length > 1
      ? parts[0][0] + parts[parts.length - 1][0]
      : name.slice(0, 2);
  return letters.toUpperCase();
}

export default function UserAvatar({
  name,
  email,
  picture,
  size = "default",
  className,
  fallbackClassName,
  "aria-hidden": ariaHidden,
}: {
  name: string;
  email?: string | null;
  picture?: string | null;
  size?: "default" | "sm" | "lg";
  className?: string;
  fallbackClassName?: string;
  "aria-hidden"?: boolean;
}) {
  const [gravatar, setGravatar] = useState<string | null>(null);
  const [failed, setFailed] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setGravatar(null);
    setFailed([]);
    if (!email || !globalThis.crypto?.subtle) return;
    void gravatarUrl(email).then((url) => {
      if (!cancelled) setGravatar(url);
    });
    return () => {
      cancelled = true;
    };
  }, [email, picture]);

  const src = [picture, gravatar].find(
    (candidate): candidate is string =>
      Boolean(candidate) && !failed.includes(candidate as string)
  );

  return (
    <Avatar size={size} className={className} aria-hidden={ariaHidden}>
      {src && (
        <AvatarImage
          key={src}
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          onLoadingStatusChange={(status) => {
            if (status === "error") setFailed((previous) => [...previous, src]);
          }}
        />
      )}
      <AvatarFallback className={cn("font-medium", fallbackClassName)}>
        {initials(name || email || "")}
      </AvatarFallback>
    </Avatar>
  );
}
